import "@autodromo/blocks/styles/globals.css";
import type { AppProps } from "next/app";
import Head from "next/head";
import Script from "next/script";
import { CookiesModal } from "@autodromo/blocks/sectionsWrapper/CookiesModal";
import { UnitSelectionModal } from "@autodromo/blocks/sectionsWrapper/UnitSelectionModal";
import {
  parseCustomHtmlTags,
  parseFooterHtmlTags,
} from "@autodromo/blocks/utils/parseScripts";
import { StyleguideStyles } from "@autodromo/blocks/components/StyleguideStyles";
import { DynamicFonts } from "@autodromo/blocks/components/DynamicFonts";
import type { SiteMetadata, Styleguide } from "@autodromo/blocks/utils/s3";
import type { Brand } from "@autodromo/blocks/utils/brands";
import { resolveAntiBotMeta } from "@autodromo/blocks/constants/antiBot";
import { WhatsappFloatingButton } from "@autodromo/blocks/components/WhatsappFloatingButton";
import { useGlobalQueryParams } from "@autodromo/blocks/hooks/queryParams/useGlobalQueryParams";
import { BrowserEventSetup } from "@autodromo/blocks/components/BrowserEventSetup";
import { CentralAnalytics } from "@autodromo/blocks/components/CentralAnalytics/CentralAnalytics";
import { AnimationObserver } from "@autodromo/blocks/components/BlockAnimation/AnimationObserver";
import { interpolate } from "@autodromo/blocks/utils/interpolate";
import { resolvePageEntry } from "@autodromo/blocks/utils/pageEntry";
import { buildStructuredData } from "@autodromo/blocks/utils/structuredData";
import { sanitizeJsonForHtml } from "@autodromo/blocks/utils/sanitize";
import { RendererApiProvider } from "@autodromo/blocks/hooks/rendererApi/useRendererApi";
import { toPathname } from "@autodromo/blocks/utils/rendererApi";
import { QueryParamsAdapterProvider } from "../components/QueryParamsAdapterProvider";
import { useRouter } from "next/router";
import { DynamicPopupRenderer } from "@autodromo/blocks/popups";
import type { PublishedPopup } from "@autodromo/blocks/popups";

interface PageSeo {
  title?: string | null;
  description?: string | null;
  canonical?: string | null;
  image?: string | null;
  socialTitle?: string | null;
  socialDescription?: string | null;
  enableNoIndex?: boolean | null;
}

interface CustomAppProps extends AppProps {
  pageProps: {
    metadata?: SiteMetadata | null;
    styleguide?: Styleguide | null;
    pageScriptsHead?: string | null;
    pageScriptsFooter?: string | null;
    pageData?: {
      showWhatsappButton?: boolean | null;
      seo?: PageSeo | null;
      moduleContentTypeId?: string | null;
      ROOT?: { dynamicContents?: Record<string, unknown> | null } | null;
      [key: string]: unknown;
    } | null;
    dynamicPageContent?: Record<string, any> | null;
    dynamicContentsData?: Record<string, any> | null;
    channelData?: Record<string, any> | null;
    brands?: Brand[] | null;
    requestHost?: string | null;
    requestPath?: string | null;
    itemListingLink?: string | null;
    canonical?: string | null;
    [key: string]: unknown;
  };
}

const CUSTOM_VISIBILITY_MODE = "CUSTOM_PER_PAGE" as const;

export default function App({ Component, pageProps }: CustomAppProps) {
  const router = useRouter();
  const browserPath = toPathname({ url: router.asPath });
  const metadata = pageProps.metadata;
  const styleguide = pageProps.styleguide;
  const popups = (pageProps.popupsData as PublishedPopup[] | null) ?? null;
  const whatsappConfig = metadata?.whatsapp;

  // SEO: página tem prioridade, channel.name é fallback
  const pageSeo = pageProps.pageData?.seo;
  const dynamicPageContent = pageProps.dynamicPageContent;
  const channelData = pageProps.channelData;

  // Estrutura para interpolação de variáveis dinâmicas (ex: {{item.name}})
  const dynamicStructure = {
    item: dynamicPageContent || {},
    channel: channelData || {},
  };

  // Interpola título e descrição se contiverem variáveis dinâmicas
  const rawTitle = pageSeo?.title || metadata?.name || "";
  const rawDescription = pageSeo?.description || "";
  const rawSocialTitle = pageSeo?.socialTitle || "";
  const rawSocialDescription = pageSeo?.socialDescription || "";
  const rawSocialImage = pageSeo?.image || "";

  // Campos de SEO/Social são texto puro: variáveis mapeadas para texto rico
  // (`<p>...</p>`) ou preço (`R$&nbsp;...`) são sanitizadas via a opção
  // `sanitizeHtml` do interpolate antes de virar <title>/description/og. ATW-230.
  const siteTitle = rawTitle
    ? interpolate(
        { structure: dynamicStructure, template: rawTitle },
        { sanitizeHtml: true },
      ).text
    : null;
  const siteDescription = rawDescription
    ? interpolate(
        { structure: dynamicStructure, template: rawDescription },
        { sanitizeHtml: true },
      ).text
    : null;
  const socialTitle = rawSocialTitle
    ? interpolate(
        { structure: dynamicStructure, template: rawSocialTitle },
        { sanitizeHtml: true },
      ).text
    : null;
  const socialDescription = rawSocialDescription
    ? interpolate(
        { structure: dynamicStructure, template: rawSocialDescription },
        { sanitizeHtml: true },
      ).text
    : null;
  const socialImage =
    rawSocialImage == null
      ? null
      : (interpolate({
          structure: dynamicStructure,
          template: rawSocialImage,
        }).image?.src ?? rawSocialImage);
  const isNoIndex = Boolean(pageSeo?.enableNoIndex || metadata?.enableNoIndex);

  // Canonical (ATW-315): valor manual do JSON prevalece sobre a auto-referente
  // computada no getServerSideProps; og:url espelha o valor resolvido.
  const canonicalUrl = pageSeo?.canonical || pageProps.canonical || null;

  const whatsappItems = whatsappConfig?.items ?? [];
  const hasWhatsappItems = whatsappItems.length > 0;

  const isVisibleByPage =
    whatsappConfig?.visibilityMode === CUSTOM_VISIBILITY_MODE
      ? pageProps.pageData?.showWhatsappButton === true
      : true;

  const isWhatsappVisible =
    whatsappConfig?.floatingButtonEnabled === true &&
    hasWhatsappItems &&
    isVisibleByPage;

  const pageEntry = resolvePageEntry({
    pageData: pageProps.pageData,
    dynamicPageContent,
  });

  const structuredData = buildStructuredData({
    channelData,
    host: pageProps.requestHost,
    dynamicPageContent,
    dynamicContents: pageProps.pageData?.ROOT?.dynamicContents,
    dynamicContentsData: pageProps.dynamicContentsData,
    pageData: pageProps.pageData,
    itemListingLink: pageProps.itemListingLink,
    brands: pageProps.brands ?? [],
    isNoIndex,
    canonicalUrl,
  });

  const channelHeadElements = metadata?.scriptsHead
    ? parseCustomHtmlTags(metadata.scriptsHead, "channel-head")
    : [];
  const pageHeadElements = pageProps.pageScriptsHead
    ? parseCustomHtmlTags(pageProps.pageScriptsHead, "page-head")
    : [];
  const channelFooterElements = metadata?.scriptsFooter
    ? parseFooterHtmlTags(metadata.scriptsFooter, "channel-footer")
    : [];
  const pageFooterElements = pageProps.pageScriptsFooter
    ? parseFooterHtmlTags(pageProps.pageScriptsFooter, "page-footer")
    : [];

  return (
    <RendererApiProvider requestPath={pageProps.requestPath ?? null} browserPath={browserPath}>
      <QueryParamsAdapterProvider>
        {/* Dynamic Google Fonts based on styleguide */}
        <DynamicFonts styleguide={styleguide ?? null} />

        {/* Styleguide CSS Variables */}
        <StyleguideStyles styleguide={styleguide ?? null} />

        {/* Scripts Head - Channel (global) */}
        {channelHeadElements.length > 0 && <Head>{channelHeadElements}</Head>}

        {/* Scripts Head - Page (específico da página) */}
        {pageHeadElements.length > 0 && <Head>{pageHeadElements}</Head>}

        {/* Site Title (página > channel fallback) */}
        {siteTitle && (
          <Head>
            <title>{siteTitle}</title>
          </Head>
        )}

        {/* Meta Description */}
        {siteDescription && (
          <Head>
            <meta
              name="description"
              content={siteDescription}
            />
          </Head>
        )}

        {/* Canonical URL + og:url (auto-referente; manual do JSON prevalece) */}
        {canonicalUrl && (
          <Head>
            <link rel="canonical" href={canonicalUrl} />
            <meta property="og:url" content={canonicalUrl} />
          </Head>
        )}

        {/* Dados estruturados (JSON-LD) */}
        {structuredData && (
          <Head>
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: sanitizeJsonForHtml(structuredData) }}
            />
          </Head>
        )}

        {/* Open Graph */}
        {(socialTitle || siteTitle) && (
          <Head>
            <meta
              property="og:title"
              content={(socialTitle || siteTitle) ?? undefined}
            />
          </Head>
        )}
        {(socialDescription || siteDescription) && (
          <Head>
            <meta
              property="og:description"
              content={(socialDescription || siteDescription) ?? undefined}
            />
          </Head>
        )}
        {socialImage && (
          <Head>
            <meta
              property="og:image"
              content={socialImage}
            />
          </Head>
        )}

        {/* Favicon */}
        <Head>
          <link
            rel="icon"
            href={metadata?.favicon || "data:,"}
          />
        </Head>

        {/* NoIndex meta tag (página > channel) */}
        {isNoIndex && (
          <Head>
            <meta
              name="robots"
              content="noindex, nofollow"
            />
          </Head>
        )}

        {/* Verificação de propriedade do Google Search Console (ATW-425).
            Só emite quando há tag salva; content={} é atributo React (auto-escapado). */}
        {metadata?.gscVerificationTag && (
          <Head>
            <meta name="google-site-verification" content={metadata.gscVerificationTag} />
          </Head>
        )}

        {/* Google Tag Manager */}
        {metadata?.googleTagManagerId && (
          <Script
            id="gtm"
            strategy="afterInteractive"
          >
            {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','${metadata.googleTagManagerId}');`}
          </Script>
        )}

        {/* AutoForce Central Analytics - gtag.js bootstrap */}
        {process.env.NEXT_PUBLIC_CENTRAL_GA4_ID &&
          !metadata?.disableCentralAnalytics && (
            <>
              <Script
                src={`https://www.googletagmanager.com/gtag/js?id=${process.env.NEXT_PUBLIC_CENTRAL_GA4_ID}`}
                strategy="afterInteractive"
              />
              <Script
                id="af-central-ga-init"
                strategy="afterInteractive"
              >
                {`window.dataLayer = window.dataLayer || [];
                if (!window.gtag) { window.gtag = function(){ dataLayer.push(arguments); }; }
                window.gtag('js', new Date());`}
              </Script>
            </>
          )}

        {/* Google Analytics (conta do cliente) */}
        {metadata?.googleAnalyticsId && (
          <>
            {/* Só carrega gtag.js se a conta central não já carregou */}
            {(!process.env.NEXT_PUBLIC_CENTRAL_GA4_ID ||
              metadata?.disableCentralAnalytics) && (
              <Script
                src={`https://www.googletagmanager.com/gtag/js?id=${metadata.googleAnalyticsId}`}
                strategy="afterInteractive"
              />
            )}
            <Script
              id="ga-init"
              strategy="afterInteractive"
            >
              {`window.dataLayer = window.dataLayer || [];
                if (!window.gtag) { window.gtag = function(){ dataLayer.push(arguments); }; }
                ${!process.env.NEXT_PUBLIC_CENTRAL_GA4_ID || metadata?.disableCentralAnalytics ? "window.gtag('js', new Date());" : ""}
                window.gtag('config', '${metadata.googleAnalyticsId}');`}
            </Script>
          </>
        )}

        {/* Facebook Pixel */}
        {metadata?.facebookPixelId && (
          <Script
            id="fb-pixel"
            strategy="afterInteractive"
          >
            {`!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
              n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}
              (window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '${metadata.facebookPixelId}');
              fbq('track', 'PageView');`}
          </Script>
        )}

        {/* Browser Event Setup (gtag, lazy loading, campaign tracking) */}
        <BrowserEventSetup />

        {/* Central Analytics (conta GA4 da AutoForce) */}
        <CentralAnalytics
          metadata={metadata ?? null}
          channelData={pageProps.channelData ?? null}
        />

        {/* Block entrance animation observer */}
        <AnimationObserver />

        {/* Main Component */}
        <Component {...pageProps} />

        {/* WhatsApp Floating Button */}
        {isWhatsappVisible && (
          <WhatsappFloatingButton
            items={whatsappItems}
            position={whatsappConfig?.floatingButtonPosition || "BOTTOM_RIGHT"}
            buttonColor={whatsappConfig?.buttonColor || "#25D366"}
            iconColor={whatsappConfig?.iconColor || "#ffffff"}
            isVisible={true}
            submitUrl={whatsappConfig?.endpointUrl || undefined}
            antiBot={resolveAntiBotMeta(metadata?.antiBot)}
            accountId={metadata?.accountId ?? undefined}
            siteId={channelData?.id}
            product={pageEntry.title}
            units={metadata?.units || []}
            unitGroupingMode={metadata?.unitGroupingMode}
            unitGroups={metadata?.unitGroups}
            useGlobalQueryParams={useGlobalQueryParams}
            pageEntry={pageEntry}
          />
        )}

        {/* Popups do site publicado (gatilhos/frequência/tracking resolvidos no cliente) */}
        {popups && popups.length > 0 && (
          <DynamicPopupRenderer
            popups={popups}
            forms={(pageProps.formsData as Record<string, unknown>) ?? {}}
            ctx={{
              accountId: metadata?.accountId ?? null,
              siteId: channelData?.id ?? null,
              channelName: channelData?.name ?? null,
              currentSlug: (router.asPath || "/").split("?")[0] || "/",
              requestPath: pageProps.requestPath ?? null,
              // Nome da página (título já interpolado/sanitizado) para a tabela
              // "Métricas por página" do admin não ficar com a coluna Página vazia.
              currentPageTitle: siteTitle || undefined,
              crmConversionsUrl: process.env.NEXT_PUBLIC_DEFAULT_FORM_SUBMIT_URL || null,
              antiBot: resolveAntiBotMeta(metadata?.antiBot),
            }}
          />
        )}

        {/* Scripts Footer - Channel (global) */}
        {channelFooterElements}

        {/* Scripts Footer - Page (específico da página) */}
        {pageFooterElements}

        {/* Cookies Modal */}
        {metadata?.enableCookies && (
          <CookiesModal
            description={
              metadata.cookieDescription ||
              "Esse site utiliza cookies para melhorar a sua experiência."
            }
            buttonText={metadata.cookieButtonText || "Aceitar"}
            buttonColor={metadata.cookieButtonColor || "#0C2AFE"}
          />
        )}

        {/* Unit Selection Modal */}
        {metadata?.unitPopupEnabled && (
          <UnitSelectionModal
            enabled={metadata.unitPopupEnabled}
            required={metadata.unitPopupRequired || false}
            title={metadata.unitPopupTitle || "Escolha sua unidade"}
            description={
              metadata.unitPopupDescription ||
              "Selecione a unidade mais próxima de você para ver os estoques disponíveis"
            }
            color={metadata.unitPopupColor || "#0C2AFE"}
            units={metadata.units || []}
            unitGroupingMode={metadata.unitGroupingMode}
            unitGroups={metadata.unitGroups}
            unitGroupingLabel={metadata.unitGroupingLabel}
          />
        )}
      </QueryParamsAdapterProvider>
    </RendererApiProvider>
  );
}